import React from 'react'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import classnames from 'classnames'
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { Field, propTypes as formPropTypes } from 'redux-form'
import { Button, Checkbox, SubmitButton } from 'lp-components'
import { lpForm } from 'lp-form'
import { AutoRenewalContent } from 'member-portal/components'
import { API_ERROR_MAP } from 'types'
import { TERMS_AGREEMENT_VALIDATOR } from 'config'
import { createPaymentElementOptions } from 'utils/stripeOptions'
import { useStripeActions } from 'utils'

const propTypes = {
  ...formPropTypes,
  closeEditForm: PropTypes.func,
  onError: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  stripeSetupIntentId: PropTypes.string.isRequired,
  submitButtonText: PropTypes.string.isRequired,
  withTerms: PropTypes.bool,
}

function StripeAutoRenewalForm({
  closeEditForm = null,
  handleSubmit,
  onError,
  onSubmit,
  stripeSetupIntentId,
  submitButtonText,
  submitting,
  withTerms = false,
}) {
  const { confirmSetupIntent, isProcessing } = useStripeActions()
  const stripe = useStripe()
  const elements = useElements()

  const handleFormSubmit = async (formValues) => {
    if (!stripe || !elements) {
      throw new Error(API_ERROR_MAP.AUTHNET_ERROR_OCCURRED)
    }

    try {
      const paymentMethodId = await confirmSetupIntent(stripeSetupIntentId)

      const updatedFormValues = {
        ...formValues,
        stripe_payment_method_id: paymentMethodId,
      }

      await onSubmit(updatedFormValues)
    } catch (error) {
      onError(error)
    }
  }

  return (
    <div className="card-inner">
      <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <div className="group-block">
          <div className="form-group">
            <label htmlFor="stripe-payment-element">
              Payment Information <span className="required-indicator">*</span>
            </label>
            <div className="stripe-payment-element-container">
              <PaymentElement
                id="stripe-payment-element"
                options={createPaymentElementOptions()}
              />
            </div>
          </div>
        </div>

        {withTerms && (
          <div className="auto-renewal-agreement-container">
            <hr className="divider" />
            <label htmlFor="termsAgreement">
              Please Acknowledge<span className="required-indicator">*</span>
            </label>
            <Field
              className="checkbox auto-renew-label"
              component={Checkbox}
              labelComponent={AutoRenewalContent}
              name="termsAgreement"
            />
          </div>
        )}

        <div className={classnames('button-group', { 'with-terms': withTerms })}>
          {closeEditForm && (
            <Button onClick={closeEditForm} className="button-primary-outline">
              Cancel
            </Button>
          )}
          <SubmitButton
            submitting={submitting || isProcessing}
            type="submit"
            disabled={!stripe || !elements}
          >
            {submitButtonText}
          </SubmitButton>
        </div>
      </form>
    </div>
  )
}

StripeAutoRenewalForm.propTypes = propTypes

export default compose(
  lpForm({
    name: 'StripeAutoRenewalForm',
    constraints: {
      termsAgreement: TERMS_AGREEMENT_VALIDATOR,
    },
  }),
)(StripeAutoRenewalForm)
